import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Star, RefreshCw, Search, Copy, ExternalLink } from "lucide-react";
import { API } from "@/config";

const inputCls =
  "w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold";
const btnCls =
  "inline-flex w-fit items-center gap-2 rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium px-6 py-3 transition-colors duration-200";

export default function ReviewsPanel({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [link, setLink] = useState("");
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () =>
    axios
      .get(`${API}/admin/reviews`, { headers })
      .then((res) => { setCfg(res.data); setLink(res.data.link); })
      .catch(() => toast.error("No se pudo cargar la configuración de reseñas"));

  useEffect(() => {
    load();
  }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res?.data?.link !== undefined) { setCfg(res.data); setLink(res.data.link); }
      if (okMsg) toast.success(okMsg);
      return res;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Algo salió mal");
    } finally {
      setBusy("");
    }
  };

  const saveLink = () => run("link", () => axios.put(`${API}/admin/reviews`, { link }, { headers }), "Enlace guardado");
  const saveKey = () =>
    run("key", () => axios.put(`${API}/admin/reviews`, { api_key: apiKey }, { headers }), "Clave guardada").then(() => setApiKey(""));
  const removeKey = () => run("offkey", () => axios.put(`${API}/admin/reviews`, { api_key: "" }, { headers }), "Clave eliminada");
  const refresh = () => run("refresh", () => axios.post(`${API}/admin/reviews/refresh`, {}, { headers }), "Reseñas actualizadas");

  const search = async () => {
    setBusy("search");
    try {
      const res = await axios.post(`${API}/admin/reviews/search`, { query }, { headers });
      setCandidates(res.data.candidates);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo buscar tu negocio");
    } finally {
      setBusy("");
    }
  };

  const pickPlace = (c) =>
    run(
      "pick",
      () => axios.put(`${API}/admin/reviews`, { place_id: c.place_id, place_name: c.name, place_address: c.address }, { headers }),
      "Negocio conectado · reseñas publicadas en tu web"
    ).then(() => setCandidates([]));

  if (!cfg) return <div className="mt-6 h-48 rounded-2xl bg-sand/50 animate-pulse" />;

  return (
    <section data-testid="admin-reviews-panel">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Reseñas de Google</h1>
      <p className="text-sm text-taupe mt-1">
        Tus reseñas reales de Google se muestran solas en la web. Además, cada clienta con correo recibe al día siguiente de su cita un email para dejarte su reseña.
      </p>

      <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8 grid gap-8">
        <div data-testid="reviews-status" className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${cfg.connected ? "bg-emerald-500" : cfg.has_key ? "bg-amber-400" : "bg-sand"}`} />
          <span className="text-sm text-cocoa">
            {cfg.connected
              ? `Conectado con ${cfg.place_name} · ${cfg.rating ?? "—"} ★ (${cfg.count ?? 0} reseñas)`
              : cfg.has_key
              ? "Clave guardada · falta elegir tu negocio"
              : "Sin conectar con Google (en la web se ven los testimonios de ejemplo)"}
          </span>
        </div>
        {cfg.error && (
          <p data-testid="reviews-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs px-4 py-2.5">{cfg.error}</p>
        )}

        <div className="grid gap-3">
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Tu enlace para pedir reseñas</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://g.page/r/…/review" data-testid="reviews-link-input" className={inputCls} />
            <button onClick={saveLink} disabled={busy === "link"} data-testid="reviews-save-link-btn" className={btnCls}>
              {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar
            </button>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <button
              onClick={() => { navigator.clipboard.writeText(cfg.link); toast.success("Enlace copiado"); }}
              data-testid="reviews-copy-link-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-2 text-cocoa hover:border-gold hover:text-gold transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copiar enlace
            </button>
            {cfg.link && (
              <a href={cfg.link} target="_blank" rel="noreferrer" data-testid="reviews-open-link" className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-2 text-cocoa hover:border-gold hover:text-gold transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Abrirlo
              </a>
            )}
          </div>
        </div>

        {!cfg.has_key ? (
          <div className="grid gap-3">
            <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Conectar tus reseñas de Google</p>
            <ol className="text-sm text-cocoa list-decimal pl-5 space-y-1">
              <li>Entra en <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="text-gold underline">Google Cloud Console</a> con tu cuenta de Google y crea un proyecto.</li>
              <li>Activa la facturación del proyecto (Google da crédito gratuito mensual; para una web pequeña no se suele pagar nada).</li>
              <li>En «APIs y servicios → Biblioteca» busca <b>Places API (New)</b> y pulsa <b>Habilitar</b>.</li>
              <li>En «APIs y servicios → Credenciales → Crear credenciales → Clave de API», copia la clave y pégala aquí.</li>
            </ol>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Clave de API de Google (AIza…)" data-testid="reviews-key-input" className={inputCls} />
              <button onClick={saveKey} disabled={!apiKey.trim() || busy === "key"} data-testid="reviews-save-key-btn" className={btnCls}>
                {busy === "key" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar clave
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-3">
            <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Tu negocio en Google</p>
            <p className="text-sm text-cocoa">
              Clave guardada: <code className="bg-cream px-1.5 py-0.5 rounded text-xs">{cfg.key_masked}</code>
              {cfg.place_id && <> · Negocio: <b>{cfg.place_name}</b> {cfg.place_address && <span className="text-taupe">({cfg.place_address})</span>}</>}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && query.trim().length > 2 && search()}
                placeholder="ISI'S Nails, calle y ciudad"
                data-testid="reviews-search-input"
                className={inputCls}
              />
              <button onClick={search} disabled={query.trim().length < 3 || busy === "search"} data-testid="reviews-search-btn" className={btnCls}>
                {busy === "search" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar mi negocio
              </button>
            </div>
            {candidates.length > 0 && (
              <div data-testid="reviews-candidates" className="rounded-xl border border-gold/40 bg-cream divide-y divide-sand">
                {candidates.map((c) => (
                  <button
                    key={c.place_id}
                    onClick={() => pickPlace(c)}
                    data-testid={`reviews-candidate-${c.place_id}`}
                    className="w-full text-left px-4 py-3 hover:bg-rosesoft transition-colors"
                  >
                    <p className="text-sm font-medium text-obsidian">{c.name}</p>
                    <p className="text-xs text-taupe">{c.address}{c.rating ? ` · ${c.rating} ★ (${c.count})` : ""}</p>
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-2">
              <button onClick={refresh} disabled={busy === "refresh"} data-testid="reviews-refresh-btn" className={btnCls}>
                {busy === "refresh" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Traer reseñas ahora
              </button>
              <button onClick={removeKey} disabled={busy === "offkey"} data-testid="reviews-remove-key-btn" className="inline-flex items-center gap-2 rounded-xl border border-sand text-taupe hover:text-obsidian text-sm px-6 py-3 transition-colors">
                Quitar clave
              </button>
            </div>
          </div>
        )}

        {cfg.reviews?.length > 0 && (
          <div className="grid gap-3">
            <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Reseñas que se ven en tu web</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {cfg.reviews.map((r, i) => (
                <div key={i} data-testid={`reviews-preview-${i}`} className="rounded-xl border border-sand bg-cream p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-obsidian">{r.author}</p>
                    <span className="inline-flex items-center gap-1 text-xs text-gold">{r.rating} <Star className="w-3 h-3 fill-gold" /></span>
                  </div>
                  <p className="text-xs text-taupe mt-1.5 whitespace-pre-line line-clamp-4">{r.text}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-taupe">Google devuelve como máximo 5 reseñas, ordenadas por relevancia. Se actualizan solas cada 15 minutos.</p>
          </div>
        )}
      </div>
    </section>
  );
}
